<script setup lang="ts">
// Tela "Conquistas" do Minha Conta V2 (ENG-362). Destino do "Ver mais" das
// conquistas do dashboard. Figma: mobile 19771:42400 (grade 3 col) /
// desktop 19831:60084 (grade 4 col), com voltar para o Perfil e paginação.
// Clicar numa conquista abre o modal de resgate (UserV2AchievementModal,
// renderizado no Shell) via useUserV2Achievements.
import { useElementSize } from '@vueuse/core';

const { list, openDetail } = useUserV2Achievements();
const { open } = useUserV2Modal();
const { isMobile } = useLayout();

// No mobile a tela inteira rola (sem h-full), então a página fixa de baixo
// não sobra/falta espaço — só o desktop (md:h-full, grid dentro de um
// container de altura fixa) precisa calcular quantos itens cabem de
// verdade, senão ou sobra espaço vazio abaixo do grid ou rola à toa.
// 12 (não 16): múltiplo de 3 E de 4 — grid-cols-3 no mobile (fixo, nunca
// recalcula) e md:grid-cols-4 no desktop (só o valor inicial, antes do
// primeiro recalculatePerPage) sempre fecham a última linha certinho, sem
// célula vazia sobrando.
const DEFAULT_PER_PAGE = 12;
const perPage = ref(DEFAULT_PER_PAGE);

const page = ref<number>(1);

const lastPage = computed(() =>
  Math.max(1, Math.ceil(list.value.length / perPage.value)),
);

const pagedAchievements = computed(() =>
  list.value.slice(
    (page.value - 1) * perPage.value,
    page.value * perPage.value,
  ),
);

const scrollContainerRef = ref<HTMLElement | null>(null);
const gridRef = ref<HTMLElement | null>(null);
const firstItemRef = ref<HTMLElement | null>(null);
const paginationWrapperRef = ref<HTMLElement | null>(null);

function setFirstItemRef(el: unknown) {
  firstItemRef.value = (el as HTMLElement) ?? null;
}

// altura de UM item (não muda com a quantidade de itens exibidos, só com a
// largura do container) — evita loop de recálculo ao trocar perPage e
// re-renderizar com um número diferente de linhas.
const { height: scrollHeight } = useElementSize(scrollContainerRef);
const { height: itemHeight } = useElementSize(firstItemRef);

function recalculatePerPage() {
  if (isMobile.value) return;

  const gridEl = gridRef.value;

  if (!gridEl || !itemHeight.value || !scrollHeight.value) {
    return;
  }

  const columns =
    getComputedStyle(gridEl).gridTemplateColumns.split(' ').length || 1;

  const rowGap = parseFloat(getComputedStyle(gridEl).rowGap || '0');
  const rowHeight = itemHeight.value + rowGap;

  // gap-[10px] do flex pai entre o grid e a paginação
  const paginationHeight = paginationWrapperRef.value
    ? paginationWrapperRef.value.offsetHeight + 10
    : 0;

  const availableHeight = scrollHeight.value - paginationHeight;

  const rows = Math.max(1, Math.floor(availableHeight / rowHeight));
  const nextPerPage = columns * rows;

  if (nextPerPage !== perPage.value) {
    perPage.value = nextPerPage;
    page.value = Math.min(page.value, lastPage.value);
  }
}

watch([scrollHeight, itemHeight, isMobile], recalculatePerPage);

onMounted(() => {
  nextTick(recalculatePerPage);
});

function backToProfile() {
  open('profile');
}
</script>

<template>
  <div
    class="border-solid flex flex-col gap-[10px] md:h-full md:min-h-0 md:rounded-[24px] md:border md:border-v2-default/40 md:p-[16px]"
  >
    <button
      class="flex items-center gap-[6px] self-start text-[14px] leading-[20px] text-white"
      type="button"
      @click="backToProfile"
    >
      <UserV2Icon name="chevron-left" />
      Conquistas
    </button>

    <div
      ref="scrollContainerRef"
      class="user-v2-scroll flex flex-col gap-[10px] md:min-h-0 md:flex-1 md:overflow-y-auto md:pr-[6px]"
    >
      <div
        ref="gridRef"
        class="grid grid-cols-3 gap-[12px] md:grid-cols-4"
      >
        <button
          v-for="(achievement, index) in pagedAchievements"
          :key="achievement.key"
          :ref="index === 0 ? setFirstItemRef : undefined"
          class="flex flex-col items-center gap-[5px]"
          type="button"
          @click="openDetail(achievement.key)"
        >
          <span
            class="border-solid relative mx-auto flex aspect-[4/5] w-[90%] items-center justify-center rounded-[20px] border border-v2-default/40 bg-v2-default/40"
          >
            <img
              alt=""
              class="size-[100px] object-contain"
              :src="achievement.icon"
            />
            <span
              v-if="achievement.claimed"
              class="absolute right-[8px] top-[8px] flex size-[20px] items-center justify-center rounded-full bg-v2-success text-white"
            >
              <svg
                class="size-[12px]"
                fill="none"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2.5"
                viewBox="0 0 24 24"
              >
                <path d="m4 12 5 5L20 6" />
              </svg>
            </span>
          </span>
          <span
            class="w-full truncate px-[5px] text-center text-[12px] font-semibold leading-[16px] text-white"
          >
            {{ achievement.name }}
          </span>
        </button>
      </div>

      <div
        v-if="lastPage > 1"
        ref="paginationWrapperRef"
        class="mt-auto"
      >
        <UserV2Pagination
          :last-page="lastPage"
          :page="page"
          @change="(newPage) => (page = newPage)"
        />
      </div>
    </div>
  </div>
</template>
